import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import NoteCard from './NoteCard';
import { Eye, EyeOff } from 'lucide-react';
import apiClient from '../services/apiClient';
import { t } from '../i18n';
import './NoteList.css';

const isCompletedNote = note => Number(note.flashcardCount) > 0 && Number(note.completedCardCount) >= Number(note.flashcardCount);

function SortableNoteCard({
  note,
  onNoteCardClick,
  onNotePractice,
  onMoveMode,
  isMoveEnabled,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: note.id, disabled: !isMoveEnabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : undefined,
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style} role="listitem">
      <div
        className={`note-card-drag-handle${isMoveEnabled ? ' is-visible' : ''}`}
        {...(isMoveEnabled ? attributes : {})}
        {...(isMoveEnabled ? listeners : {})}
        aria-hidden={!isMoveEnabled}
        title={t('dashboard.notes.reorder')}
      >
        ⠿
      </div>
      <NoteCard
        note={note}
        onClick={onNoteCardClick}
        onPractice={onNotePractice}
        onEdit={onNoteCardClick}
        onMoveMode={onMoveMode}
        isMoveEnabled={isMoveEnabled}
      />
    </div>
  );
}

function NoteList({ notes, onNoteClick, onNotePractice, isLoading = false }) {
  const [noteMetadata, setNoteMetadata] = useState([]);
  const [orderedIds, setOrderedIds] = useState(null);
  const [moveNoteId, setMoveNoteId] = useState(null);
  const [showCompleted, setShowCompleted] = useState(true);
  const noteIdsKey = useMemo(() => (notes || []).map(note => note.id).join('|'), [notes]);

  useEffect(() => {
    let cancelled = false;
    apiClient.getAllNotes().then(metadata => {
      if (!cancelled) setNoteMetadata(Array.isArray(metadata) ? metadata : []);
    }).catch(err => console.error('Failed to fetch note metadata:', err));
    return () => { cancelled = true; };
  }, [noteIdsKey]);

  // Merge coverUrl + sortOrder from API metadata into the notes prop
  const mergedNotes = useMemo(() => {
    if (!notes) return [];
    const metaMap = new Map(noteMetadata.map(m => [m.noteId, m]));
    return notes.map(note => {
      const meta = metaMap.get(note.id) || {};
      return {
        ...note,
        coverUrl: meta.coverUrl || note.coverUrl || null,
        isEnabled: meta.isEnabled !== undefined ? meta.isEnabled !== false : note.isEnabled !== false,
        sortOrder: meta.sortOrder ?? note.sortOrder ?? 9999,
      };
    });
  }, [notes, noteMetadata]);

  // Build a lookup: id → note
  const noteById = useMemo(() => new Map(mergedNotes.map(n => [n.id, n])), [mergedNotes]);

  // Initialise orderedIds once (uses backend sortOrder, falls back to arrival order)
  // Append any new note IDs not yet in orderedIds so freshly added notes appear immediately
  useEffect(() => {
    if (mergedNotes.length === 0) return;
    setOrderedIds(prev => {
      if (prev === null) {
        return [...mergedNotes]
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map(n => n.id);
      }
      const existingSet = new Set(prev);
      const newIds = mergedNotes.map(n => n.id).filter(id => !existingSet.has(id));
      return newIds.length > 0 ? [...newIds, ...prev] : prev;
    });
  }, [mergedNotes]);

  const displayNotes = useMemo(() => {
    if (!orderedIds) return mergedNotes;
    return orderedIds.map(id => noteById.get(id)).filter(Boolean);
  }, [mergedNotes, noteById, orderedIds]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } })
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setOrderedIds(prev => {
      if (!prev) return prev;
      const oldIndex = prev.indexOf(active.id);
      const newIndex = prev.indexOf(over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      // persist in background
      apiClient.reorderNotes(next).catch(err =>
        console.error('Failed to save note order:', err)
      );
      return next;
    });
  }, []);

  const handleNoteCardClick = (noteId) => {
    if (onNoteClick) onNoteClick(noteId);
  };

  const handleNotePractice = (noteId) => {
    if (onNotePractice) onNotePractice(noteId);
  };

  const handleMoveMode = (noteId) => {
    setMoveNoteId(prev => (prev === noteId ? null : noteId));
  };

  if (isLoading) {
    return (
      <div className="note-list-container">
        <div className="note-list-loading" data-testid="note-list-loading" role="status" aria-live="polite">
          <div className="note-list-spinner"></div>
          <p>{t('dashboard.notes.loading')}</p>
        </div>
      </div>
    );
  }

  if (!notes || notes.length === 0) {
    return (
      <div className="note-list-container">
        <div className="note-list-empty" role="status">
          <div className="note-list-empty-icon" aria-hidden="true">📝</div>
          <h3 className="note-list-empty-title">{t('dashboard.notes.emptyTitle')}</h3>
          <p className="note-list-empty-message">
            {t('dashboard.notes.emptyMessage')}
          </p>
        </div>
      </div>
    );
  }

  const completedCount = displayNotes.filter(isCompletedNote).length;
  const visibleNotes = showCompleted ? displayNotes : displayNotes.filter(note => !isCompletedNote(note));
  const dragItems = visibleNotes.map(n => n.id);

  return (
    <div className="note-list-container" data-testid="note-list">
      <div className="note-list-summary">
      <button type="button" className="note-list-completed-toggle" aria-pressed={showCompleted}
        title={t(showCompleted ? 'dashboard.notes.hideCompleted' : 'dashboard.notes.showCompleted')}
        onClick={() => { setShowCompleted(value => !value); setMoveNoteId(null); }}>
        {showCompleted ? <Eye size={15} aria-hidden="true" /> : <EyeOff size={15} aria-hidden="true" />}
        {t('dashboard.notes.completedCount', undefined, { count: completedCount })}
      </button>
      <span className="note-list-count">
        {t(notes.length === 1 ? 'dashboard.notes.countOne' : 'dashboard.notes.countMany', undefined, { count: notes.length })}
      </span>
      </div>
      {!visibleNotes.length && <p role="status" className="note-list-filter-empty">{t('dashboard.notes.completedHidden')}</p>}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={dragItems} strategy={rectSortingStrategy}>
          <div className="note-grid" data-testid="note-grid" role="list">
            {visibleNotes.map(note => (
              <SortableNoteCard
                key={note.id}
                note={note}
                onNoteCardClick={handleNoteCardClick}
                onNotePractice={handleNotePractice}
                onMoveMode={handleMoveMode}
                isMoveEnabled={moveNoteId === note.id}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

export default NoteList;
